import { useState, useEffect, useMemo } from "react";
import { Dropdown, Button, ButtonGroup } from "react-bootstrap";
import { ISearchDropDownProps } from "./Interfaces";
import "./css/SearchDropDown.css";

const SearchDropDown = ({
  options,
  disabled = false,
  clear,
  close,
  showCode = false,
  defaultValue = "Select",
  setOpen,
  onSelect,
}: ISearchDropDownProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownTitle, setDropdownTitle] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);

  const labels = options.map((option) =>
    showCode ? `${option.key} / ${option.label}` : option.label
  );

  const filteredOptions = useMemo(() => {
    return options.filter((option) => {
      const label = showCode ? `${option.key} / ${option.label}` : option.label;
      return label.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [options, searchTerm]);

  useEffect(() => {
    if (close === true) {
      setIsOpen(false);
    }
  }, [close]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as Element).closest(".dropdown")) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    if (setOpen) {
      setOpen(isOpen);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    setDropdownTitle(defaultValue);
  }, [options, defaultValue]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSelect = (eventKey: string | null) => {
    if (eventKey === "deselect") {
      setDropdownTitle("Select");
    } else {
      const selectedOption = options.find((option) => option.key === eventKey);
      if (selectedOption) {
        let title = "";
        if (selectedOption.label) {
          title =
            showCode && selectedOption.key
              ? `${selectedOption.key} / ${selectedOption.label}`
              : selectedOption.label;
        } else if (showCode && selectedOption.key) {
          title = selectedOption.key;
        }
        setDropdownTitle(title);
      }
    }
    if (onSelect) {
      const label = options.find((option) => option.key === eventKey)?.label;
      onSelect(eventKey ? eventKey : "deselect", label ? label : "deselect");
    }
    setIsOpen(false);
    setSearchTerm("");
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    setDropdownTitle(defaultValue);
    setIsOpen(false);
  }, [clear, defaultValue]);

  return (
    <div>
      <Dropdown
        className={"dropdown"}
        onSelect={handleSelect}
        as={ButtonGroup}
        show={isOpen}
        autoClose={true}
      >
        <Button
          variant="primary"
          className={"fixed-button"}
          disabled={disabled}
          onClick={toggleDropdown}
        >
          {dropdownTitle}
        </Button>
        <Dropdown.Toggle
          split={false}
          variant="primary"
          id="dropdown-split-basic"
          disabled={disabled}
          onClick={toggleDropdown}
        />
        <Dropdown.Menu align={{ lg: "start" }} rootCloseEvent={"mousedown"}>
          <Dropdown.Header className={"fixed-header"}>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search..."
              className="form-control"
              disabled={disabled}
            />
          </Dropdown.Header>
          <div className={"dropdown-items"}>
            <Dropdown.Item eventKey="deselect">Deselect</Dropdown.Item>
            {filteredOptions.map((option) => (
              <Dropdown.Item key={option.key} eventKey={option.key}>
                {showCode && option.key}
                {showCode && option.label && " / "}
                {option.label}
              </Dropdown.Item>
            ))}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default SearchDropDown;
